/**
 * Datasheet presentation — a table of records, not a database screen.
 *
 * PostgreSQL stays the source of truth. Nothing here renames a column, edits a
 * schema, or changes what a row holds: this is a PRESENTATION layer between
 * `/api/table-designer/tables/{schema}/{table}/data` and the Datasheet View.
 *
 * One live column becomes one presented column, and one stored value becomes
 * one cell a person can read:
 *
 *   patient_id       Patient Name        Jordan Blake
 *   start_at         Start At            09/11/2026 10:45 AM
 *   is_demo          Is Demo             ✓
 *   id               ID                  c11a0004…
 *
 * The stored value is never thrown away. Every cell that reads as something
 * else carries the real value in its tooltip, so the UUID behind `Jordan
 * Blake` is one hover away.
 */

import { format, isValid, parseISO } from "date-fns";
import { describeField, partitionFields } from "@/lib/formDesignerFields";

const DATE_FORMAT = "MM/dd/yyyy";
const TIMESTAMP_FORMAT = "MM/dd/yyyy h:mm a";

const DATE_TYPES = new Set(["date"]);
const TIMESTAMP_TYPES = new Set(["timestamp", "timestamptz"]);
const TIME_TYPES = new Set(["time", "timetz"]);
const NUMBER_TYPES = new Set(["integer", "bigint", "smallint", "numeric", "real", "double precision"]);

/**
 * One datasheet column as the sheet presents it. The Form Designer's field
 * presentation is reused so a column reads the same way everywhere in the
 * product: `patient_id` is `Patient Name` in the Fields panel and in the
 * sheet header alike.
 */
export function describeColumn(column) {
  const presented = describeField(column);
  return {
    ...presented,
    align: NUMBER_TYPES.has(presented.dataType.toLowerCase()) ? "right" : "left",
    isLookup: Boolean(presented.relationship),
  };
}

export function describeColumns(columns) {
  return (columns || []).filter((column) => column?.name).map(describeColumn);
}

/** Technical columns are kept, never dropped — they are only held back. */
export function partitionColumns(presented) {
  return partitionFields(presented);
}

/**
 * What a header means, for a tooltip: the database column, its live type and
 * the row a key points at. The sheet itself stays readable.
 */
export function columnDetailLines(presented) {
  if (!presented) return [];
  const lines = [`Database field: ${presented.databaseColumn}`];
  if (presented.dataType) lines.push(`Type: ${presented.dataType.toUpperCase()}`);
  if (presented.relationship) {
    lines.push(`Related table: ${presented.relationship.table}`);
    lines.push(`Related field: ${presented.relationship.column}`);
    const shows = presented.relationship.displayVia || presented.relationship.displayColumn;
    if (shows) lines.push(`Shows: ${shows}`);
    if (presented.relationship.secondaryColumn) {
      lines.push(`Also identified by: ${presented.relationship.secondaryColumn}`);
    }
  }
  if (presented.isTechnical) lines.push("Technical field");
  if (presented.description) lines.push(presented.description);
  return lines;
}

export function columnTooltip(presented) {
  return columnDetailLines(presented).join("\n");
}

/** A key shortened to something a person can compare at a glance. */
export function shortKey(value) {
  const raw = String(value ?? "");
  return raw.length > 10 ? `${raw.slice(0, 8)}…` : raw;
}

function formatIso(value, pattern) {
  const parsed = parseISO(String(value));
  return isValid(parsed) ? format(parsed, pattern) : String(value);
}

function formatTimeOfDay(value) {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(value));
  if (!match) return String(value);
  const hours = Number(match[1]);
  const suffix = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${match[2]} ${suffix}`;
}

function relationshipCell(value, presented, lookup) {
  const entry = (lookup || {})[String(value)] || null;
  const related = presented.relationship;
  const stored = `${related.table}.${related.column}: ${value}`;
  if (!entry || !entry.label) {
    // The related row exists but says nothing about itself. The key is
    // preserved and shown short rather than replaced with a guess.
    return { text: shortKey(value), title: stored, tone: "muted" };
  }
  const title = [entry.label];
  if (entry.secondary) title.push(`${related.secondaryColumn || "Identifier"}: ${entry.secondary}`);
  title.push(stored);
  return { text: entry.label, title: title.join("\n"), tone: "" };
}

/**
 * One stored value as one cell: `{ text, title, tone }`.
 *
 * `tone` is `muted` when the text stands in for a value the sheet cannot
 * name — an unnamed related row, or a raw key the user asked to see.
 */
export function cellDisplay(value, presented, lookup) {
  if (value === null || value === undefined) return { text: "", title: "", tone: "" };
  const dataType = String(presented?.dataType || "").toLowerCase();
  if (presented?.relationship) return relationshipCell(value, presented, lookup);
  if (typeof value === "boolean") return { text: value ? "✓" : "", title: value ? "Yes" : "No", tone: "" };
  if (typeof value === "object" && value.kind === "json") {
    return { text: value.preview, title: `${value.chars} characters of JSON`, tone: "muted" };
  }
  if (TIMESTAMP_TYPES.has(dataType)) {
    return { text: formatIso(value, TIMESTAMP_FORMAT), title: String(value), tone: "" };
  }
  if (DATE_TYPES.has(dataType)) return { text: formatIso(value, DATE_FORMAT), title: String(value), tone: "" };
  if (TIME_TYPES.has(dataType)) return { text: formatTimeOfDay(value), title: String(value), tone: "" };
  if (dataType === "uuid") return { text: shortKey(value), title: String(value), tone: "muted" };
  return { text: String(value), title: "", tone: "" };
}

/**
 * How many records the sheet is showing, said the way a person would say it.
 */
export function recordSummary(sheet) {
  const total = Number(sheet?.total || 0);
  const shown = (sheet?.rows || []).length;
  if (!total) return "No records";
  if (shown >= total) return `${total} record${total === 1 ? "" : "s"}`;
  const from = Number(sheet?.offset || 0) + 1;
  return `${from}–${from + shown - 1} of ${total} records`;
}
